import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, RefreshCw, Link2, Unplug, Loader2, Info } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function CalendarConnect() {
  const { activeId, loadHouseholds } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [ics, setIcs] = useState("");
  const [busy, setBusy] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  const load = useCallback(async () => {
    if (!activeId) return;
    try { const { data } = await api.get("/calendar/status", { params: { household_id: activeId } }); setStatus(data); setIcs(data.ics_url || ""); }
    catch { /* optional */ }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const q = new URLSearchParams(location.search).get("calendar");
    if (q === "connected") toast.success("Google Calendar connected — syncing your busy nights");
    if (q === "error") toast.error("Couldn't connect Google Calendar — give it another go");
  }, [location.search]);

  const connect = async () => {
    try { const { data } = await api.get("/calendar/connect"); window.location.href = data.url; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const disconnect = async () => { await api.post(`/calendar/disconnect?household_id=${activeId}`); toast("Google Calendar disconnected"); await load(); };
  const saveIcs = async () => {
    setBusy(true);
    try { await api.post("/calendar/ics", { household_id: activeId, url: ics.trim() }); toast.success(ics.trim() ? "Calendar link saved" : "Calendar link removed"); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/calendar/sync", { household_id: activeId });
      toast.success(data.busy_nights_auto.length ? `Busy nights: ${data.busy_nights_auto.map((d) => d.slice(0, 3)).join(", ")}` : "No hectic evenings spotted this week — nice!");
      await loadHouseholds(); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fmtDay = (day) => {
    const iso = status?.busy_dates?.[day];
    if (!iso) return day.slice(0, 3);
    const d = new Date(iso + "T12:00:00");
    return `${day.slice(0, 3)} ${d.getDate()} ${d.toLocaleString("en-AU", { month: "short" })}`;
  };

  if (!status) return null;
  const canSync = status.google_connected || status.ics_url;

  return (
    <div className="bg-white rounded-3xl border-2 border-[#FFF3F0] shadow-soft p-4" data-testid="calendar-connect">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-[#DEE7F5] flex items-center justify-center text-blue-500"><CalendarDays size={18} strokeWidth={2.5} /></span>
        <div className="flex-1">
          <p className="font-heading font-semibold text-sorted-ink">Busy nights from your calendar</p>
          <p className="text-xs text-sorted-slate">Evenings with something on (4–8:30pm) get quick dinners automatically.</p>
        </div>
      </div>

      {status.synced_at && (
        <div className="mt-4 bg-white rounded-2xl border-2 border-[#FDECEF] p-3" data-testid="busy-evenings">
          <p className="text-xs font-bold text-sorted-ink mb-1.5">Busy evenings this week</p>
          {status.connected_members?.length > 0 && !status.google_connected && <p className="text-xs font-semibold text-sorted-slate mb-1.5" data-testid="busy-partner-note">From {status.connected_members.join(" & ")}'s Google Calendar — connect yours too and Millie counts both.</p>}
          {status.busy_nights_auto?.length ? (
            <ul className="space-y-1">
              {status.busy_nights_auto.map((day) => (
                <li key={day} className="text-sm font-semibold text-sorted-ink flex gap-2" data-testid={`busy-evening-${day}`}>
                  <span className="text-sorted-slate shrink-0">{fmtDay(day)}:</span>
                  <span className="min-w-0 truncate">{(status.busy_events?.filter((e) => e.day === day).map((e) => `${e.title} ${e.start} – ${e.end}`) || []).join(", ") || (status.reasons?.[day] || ["Busy"]).join(", ")}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm font-semibold text-sorted-slate" data-testid="busy-evenings-none">No busy evenings found</p>
          )}
        </div>
      )}

      <div className="mt-4 space-y-3">
        {status.google_enabled === false ? null : status.google_configured ? (
          status.google_connected ? (
            <div className="flex items-center justify-between bg-sorted-sage/40 rounded-2xl px-3 py-2.5" data-testid="google-connected">
              <span className="text-sm font-bold text-sorted-ink">✓ Google Calendar connected{status.connected_members?.length > 1 ? ` · ${status.connected_members.join(" & ")}` : ""}</span>
              <button onClick={disconnect} data-testid="google-disconnect" className="text-xs font-bold text-sorted-slate flex items-center gap-1"><Unplug size={13} strokeWidth={2.5} /> Disconnect</button>
            </div>
          ) : (
            <button onClick={connect} data-testid="google-connect" className="w-full bg-white border-2 border-[#FFF3F0] text-sorted-ink font-bold py-3 rounded-full flex items-center justify-center gap-2">
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4" /> Connect Google Calendar
            </button>
          )
        ) : (
          <div className="bg-[#FFF6F1] rounded-2xl p-3" data-testid="google-not-configured">
            <button onClick={() => setShowSetup(!showSetup)} className="w-full flex items-center gap-2 text-left text-sm font-bold text-sorted-ink">
              <Info size={15} strokeWidth={2.5} className="text-sorted-rose" /> Google Calendar needs a one-time setup {showSetup ? "▴" : "▾"}
            </button>
            {showSetup && (
              <ol className="mt-2 text-xs text-sorted-slate list-decimal list-inside space-y-1" data-testid="google-setup-steps">
                <li>Go to console.cloud.google.com → create a project "Sorted"</li>
                <li>APIs &amp; Services → Library → enable <b>Google Calendar API</b></li>
                <li>OAuth consent screen → External → add your Gmail as a test user → scope <code>calendar.events.readonly</code></li>
                <li>Credentials → OAuth client ID (Web) → redirect URI: <code className="break-all">{status.redirect_uri}</code></li>
                <li>Send Millie's builder the Client ID + Secret to switch this on</li>
              </ol>
            )}
          </div>
        )}

        <div>
          <p className="text-xs font-bold text-sorted-slate mb-1.5 flex items-center gap-1"><Link2 size={12} strokeWidth={2.5} /> Or paste a calendar link (.ics / webcal from Apple, Outlook or Google)</p>
          <div className="flex items-center gap-2">
            <input data-testid="ics-input" value={ics} onChange={(e) => setIcs(e.target.value)} placeholder="webcal://… or https://….ics"
              className="flex-1 min-w-0 bg-muted rounded-2xl px-3 py-2.5 text-sm font-medium text-sorted-ink outline-none focus:ring-2 focus:ring-sorted-peach" />
            <button onClick={saveIcs} disabled={busy} data-testid="ics-save" className="bg-sorted-peach text-sorted-ink text-xs font-bold px-3 py-2.5 rounded-full">Save</button>
          </div>
        </div>

        <button onClick={sync} disabled={!canSync || busy} data-testid="calendar-sync"
          className="w-full bg-sorted-rose disabled:opacity-40 text-white font-bold py-3 rounded-full flex items-center justify-center gap-2">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} strokeWidth={2.5} />} Sync busy nights now
        </button>
        {status.synced_at && (
          <p className="text-xs text-sorted-slate" data-testid="calendar-synced">
            Last sync {new Date(status.synced_at).toLocaleString("en-AU", { weekday: "short", hour: "numeric", minute: "2-digit" })} ·
            {status.busy_nights_auto?.length ? ` busy: ${status.busy_nights_auto.map((d) => d.slice(0, 3)).join(", ")}` : " no hectic nights found"}
          </p>
        )}
      </div>
    </div>
  );
}
